import { useEffect, useRef, useState } from "react";
import { PaymentResponse } from "../types/payment";
import { getFormDataFromUrl } from "../utils/urlParams";
import { checkPaymentStatus } from "../utils/paymentApi";

interface UsePaymentStatusReturn {
  isPolling: boolean;
  paymentStatus: string | null;
  startPolling: (paymentId: string) => void;
  stopPolling: () => void;
  error: string | null;
}

interface UsePaymentStatusProps {
  onSucess: () => void;
  setPixPaymentData: React.Dispatch<
    React.SetStateAction<PaymentResponse | null>
  >;
  setPaymentValue: React.Dispatch<React.SetStateAction<number>>;
}

export const usePaymentStatus = ({
  onSucess,
  setPixPaymentData,
}: UsePaymentStatusProps): UsePaymentStatusReturn => {
  const [isPolling, setIsPolling] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use useRef to store the interval ID - this persists across re-renders
  const pollingIntervalRef = useRef<number | null>(null);
  const attemptsRef = useRef<number>(0);

  const POLLING_INTERVAL = 8000;
  const MAX_POLLING_ATTEMPTS = 15;

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    attemptsRef.current = 0;
  };

const startPolling = (paymentId: string) => {
  // Clear any existing interval first
  stopPolling();

  setIsPolling(true);
  attemptsRef.current = 0;
  setError(null);

  const { Id } = getFormDataFromUrl(); // 👈 pega o Id da URL

  const interval = setInterval(async () => {
    attemptsRef.current++;
    try {
      // agora passa paymentId e Id
      const response = await checkPaymentStatus(paymentId, Id);

      if (
        response.status === "RECEIVED" ||
        response.status === "RECEIVED_IN_CASH"
      ) {
        stopPolling();
        onSucess();
        setPaymentStatus(response.status);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";
      console.error("Erro ao verificar status do pagamento:", errorMessage);
      setError(errorMessage);
      stopPolling();
    }

    if (attemptsRef.current >= MAX_POLLING_ATTEMPTS) {
      stopPolling();
      setPixPaymentData(null);
      return;
    }
  }, POLLING_INTERVAL);

  pollingIntervalRef.current = interval;
};

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return {
    isPolling,
    paymentStatus,
    error,
    startPolling,
    stopPolling,
  };
};
